export const ApiHttpPaths = {
  GET: {
    analyte: {
      classes: () => ({ protected: false, url: '/analytes/classes' }),
      class: (id) => ({ protected: false, url: `/analytes/classes/${id}` }),
      parameterByClassId: (id) => ({ protected: false, url: `/analytes/classes/${id}` }),
      parameters: () => ({ protected: false, url: `/analytes/parameters` }),
      parameter: (id) => ({ protected: false, url: `/analytes/parameter/${id}` }),
    },
    appliances: {
      all: () => ({ protected: false, url: '/appliances' }),
    },
    urlMap: (code) => ({ protected: false, url: `/services/urls/${code}` }),
    users: {
      all: () => ({ protected: false, url: '/users' }),
      user: (id: string) => ({
        protected: true,
        url: `/users/${id}`,
      }),
      testKitRequests: (id: string) => ({
        protected: true,
        url: `/users/${id}/water-test-kit-requests`,
      }),
      testKitResults: (id: string) => ({
        protected: true,
        url: `/users/${id}/water-test-kit-results`,
      }),
    },
    pubWaterSystems: {
      all: () => ({ protected: false, url: '/sdwa/pub-water-systems' }),
      pubWaterSystem: (id: string) => ({
        protected: true,
        url: `/sdwa/pub-water-systems/${id}`,
      }),
      violationEnforcements: (id: string) => ({
        protected: true,
        url: `/sdwa/pub-water-systems/${id}/violation-enforcements`,
      }),
      eventMilestones: (id: string) => ({
        protected: true,
        url: `/sdwa/pub-water-systems/${id}/event-milestones`,
      }),
      eventMilestonesWDesc: (id: string) => ({
        protected: true,
        url: `/sdwa/pub-water-systems/${id}/event-milestones/details`,
      })
    },
    violationEnforcements: {
      byPwsId: {
        grouped: {
          byViolation: (pwsId: string) => ({
            protected: true,
            url: `/sdwa/pub-water-systems/${pwsId}/violation-enforcements/groups/violation`,
          }),

        }
      }
    },
    waterSystems: {
      americanStates: () => ({
        protected: false,
        url: '/epa/states',
      }),
      publicWaterSystems: (zips: string) => ({
        protected: false,
        url: `/epa/waterSystems/public?zips=${encodeURIComponent(zips)}`,
      }),
      wellWaterSystems: (zips: string) => ({
        protected: false,
        url: `/epa/waterSystems/wells?zips=${encodeURIComponent(zips)}`,
      }),
      publicWaterSystemsByAddress: (latitude: number, longitude: number, radius?: number) => ({
        protected: false,
        url: `/epa/waterSystems/public/byAddress?address[latitude]=${latitude}&address[longitude]=${longitude}${radius ? `&address[radius]=${radius}` : ''}`,
      }),
      publicWaterSystemsByAddressComponent: (
        city: string,
        state: string,
        zipCode: string,
        address1: string,
      ) => ({
        protected: false,
        url: `/epa/waterSystems/public/byAddressComponents?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&zipCode=${encodeURIComponent(zipCode)}&address1=${encodeURIComponent(address1)}`,
      }),
      publicWaterSystemMetrics: () => ({
        protected: false,
        url: `/epa/waterSystems/metrics/public`,
      }),
      wellWaterSystemMetrics: () => ({
        protected: false,
        url: `/epa/waterSystems/metrics/well`,
      }),
      mapPublicWaterSystemMetrics: () => ({
        protected: false,
        url: `/epa/waterSystems/metrics/map/public`,
      }),
      mapWellWaterSystemMetrics: () => ({
        protected: false,
        url: `/epa/waterSystems/metrics/map/well`,
      }),
      publicWaterSystemAggregates: (zoomLevel: string, stateCode?: string) => ({
        protected: false,
        url: `/epa/waterSystems/systems/public?zoomLevel=${encodeURIComponent(zoomLevel)}${stateCode ? `&stateCode=${encodeURIComponent(stateCode)}` : ''}`,
      }),
      wellWaterSystemAggregates: (zoomLevel: string, stateCode?: string, stateName?: string) => ({
        protected: false,
        url: `/epa/waterSystems/systems/well?zoomLevel=${encodeURIComponent(zoomLevel)}${stateCode ? `&stateCode=${encodeURIComponent(stateCode)}` : ''}${stateName ? `&stateName=${encodeURIComponent(stateName)}` : ''}`,
      }),
      UtilityDetails: (pwsid: string) => ({
        protected: false,
        url: `/epa/waterSystems/${pwsid}/contaminants/violations`,
      }),
    },
    waterTestKitRequests: {
      waterTestKitRequests: () => ({ protected: false, url: `/water-test-kit-requests` }),
    },
    waterTestStrips: {
      result: (jobId: string) => ({
        protected: false,
        url: `/epa/waterTestStrips/${jobId}`,
      }),
      analysis: () => ({ protected: false, url: `/water-test-analysis` }),
      analysisById: (id: string) => ({ protected: false, url: `/water-test-analysis/${id}` }),
    },
    waterTestParams: {
      parameters: () => ({
        protected: false,
        url: `/epa/waterTestParams`,
      }),
      parameterDetails: (paramName: string) => ({
        protected: false,
        url: `/epa/waterTestParams/${paramName}`,
      }),
    },
    waterQualityIndex: {
      byUserId: (userId: string) => ({
        protected: true,
        url: `/users/${userId}/water-quality-index`,
      }),
    },
  },
  POST: {
    urlMaps: () => ({ protected: false, url: '/services/urls' }),
    users: {
      // SIPP-611: fire WQI Report Completed event to Klaviyo + Customer.io
      wqiReportCompleted: (userId: string) => ({
        protected: true,
        url: `/users/${userId}/events/wqi-report-completed`,
      }),
      // SIPP-809: waitlist capture — extends Klaviyo + CIO profile with the
      // {product_type}_waitlist boolean attribute.
      waitlistSignup: (userId: string) => ({
        protected: true,
        url: `/users/${userId}/events/waitlist-signup`,
      }),
      // SIPP-844: user clicked "Stay on Free" on the Recommendations tab.
      // Backend forwards to Customer.io as the `free_plan_selected` event.
      freePlanSelected: (userId: string) => ({
        protected: true,
        url: `/users/${userId}/events/free-plan-selected`,
      }),
    },
    equipment: {
      // SIPP-807/808: Earthsmart refrigerator brand/model → filter code lookup.
      refrigeratorMatch: () => ({
        protected: true,
        url: '/api/equipment/refrigerator/match',
      }),
    },
    waterTestStrips: {
      upload: () => ({
        protected: false,
        url: '/epa/waterTestStrips',
      }),
      manualAnalyze: () => ({
        protected: false,
        url: '/water-test-strips/analysis',
      }),
      waterTestStripRequest: () => ({
        protected: false,
        url: '/water-test-kit-requests',
      }),
    },

    verification: {
      createOtp: () => ({
        protected: false,
        url: '/auth/otp',
      }),
      verifyOtp: () => ({
        protected: false,
        url: '/auth/otp/verify',
      }),
    },
    share: {
      results: () => ({
        protected: true,
        url: '/share/results',
      }),
      token: () => ({
        protected: true,
        url: '/share/token',
      }),
    },
  },
  PUT: {
    // TODO: remove when v1 strips are fully phased out // SIPP-642
    users: {
      byId: (id: string) => ({
        protected: true,
        url: `/users/${id}`,
      }),
    },
    waterTestKitRequests: {
      byId: (id: string) => ({
        protected: false,
        url: `/water-test-kit-requests/${id}`
      })
    },
    waterSystems: {
      publicWaterSystemCoordinates: () => ({
        protected: false,
        url: '/epa/waterSystems/coordinates/public',
      }),
      wellWaterSystemCoordinates: () => ({
        protected: false,
        url: '/epa/waterSystems/coordinates/well',
      }),
      wellWaterSystemGeoBlocks: () => ({
        protected: false,
        url: '/epa/waterSystems/geoblocks/well',
      }),
    },
  },
  DELETE: {},
}
